import { YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useId, useMemo } from 'react'
import { SelectAndLinkOptions } from 'site/Renderer/components/DataTables/types'
import { useSelectAndLink } from 'site/Renderer/components/SelectAndLink'
import { targetComponents } from 'site/Renderer/components/SelectAndLink/SelectAndLink.constants'
import { useFetchYesObjects } from 'yesObject/shared'
import getRelationCounterpartField from '../../lib/getRelationCountertpartField/getRelationCounterpartField'
import { relationObjectsAtColumn } from '../../lib/relationObjectsAtColumn'
import RelationAutocomplete from '../RelationAutocomplete/RelationAutocomplete'
import { RelationClassProps } from './RelationClass.types'
import RelationClassTable from './RelationClassTable'
import useRelationClassCardAutoEditEffect from './useRelationClassCardAutoEditEffect'
import useRelationClassLocalRelationState from './useRelationClassLocalRelationState'
import useRelationClassRelationSave from './useRelationClassRelationSave'
import useRelationClassRelationUnlink from './useRelationClassRelationUnlink'
import useRelationClassSelectAndLinkFieldSync from './useRelationClassSelectAndLinkFieldSync'

const RELATION_TABLE_PAGE_SIZE = 100

const RelationClass = ({
  fieldType = 'table',
  relationFieldRelation,
  fieldValues,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  field,
  informational,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  fieldSettings,
  hideInsertAction = false,
  hideSelectAndLinkAction = false,
  hideDocumentUploadAction = true,
  selectAndLinkTargetPath,
  uploadAcceptedFileTypes,
  isEditing = false,
  edit,
  onChange,
  changeAndSave,
  serverFieldValues
}: RelationClassProps) => {
  const objectclassUuid = relationFieldRelation?.uuid_objectclass || ''

  const relationCounterpartField = getRelationCounterpartField({
    relationFieldRelation,
    fieldValues
  })

  const [relationCounterpartColumn] = Object.keys(
    relationCounterpartField || {}
  )

  const {
    yesObjects,
    isFetching,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects: refetchObjects
  } = useFetchYesObjects({
    initialPagination: { limit: RELATION_TABLE_PAGE_SIZE, offset: 0 },
    filterBody: {
      query: {
        $and: [
          {
            [`${relationCounterpartColumn}.uuid`]: {
              $in: [`${fieldValues?.uuid}`]
            },
            'situation.des': {
              $gte: '40'
            }
          }
        ]
      },
      objectclass: objectclassUuid
    },
    enabled: !!relationCounterpartColumn && !!objectclassUuid
  })

  const { getSession } = useSelectAndLink()

  const { currentField, selectAndLinkSelectedOptions } = useMemo(() => {
    const currentField = field?.column_json || ''
    const currentFieldSession = getSession(currentField)
    const selectAndLinkSelectedOptions =
      currentFieldSession?.isCompleted &&
      Array.isArray(currentFieldSession.selectedOptions)
        ? currentFieldSession.selectedOptions
        : undefined
    return { currentField, currentFieldSession, selectAndLinkSelectedOptions }
  }, [field?.column_json, getSession])

  const currentRelationFromParent = useMemo(
    () => relationObjectsAtColumn(fieldValues, currentField),
    [fieldValues, currentField]
  )

  const id = useId()

  const relationId = currentField || id

  const tableId = currentField || id

  const onScrollToRelationTable = useCallback(() => {
    const relationTable = document.getElementById(relationId)

    if (relationTable) {
      relationTable.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [relationId])

  const {
    unsavedObjects,
    setUnsavedObjects,
    savedObjectsToRemove,
    setSavedObjectsToRemove,
    relationWorkingSet
  } = useRelationClassLocalRelationState(currentRelationFromParent, isEditing)

  useRelationClassCardAutoEditEffect({
    fieldType,
    isEditing,
    isFetching,
    unsavedObjects,
    yesObjects,
    edit
  })

  useRelationClassSelectAndLinkFieldSync({
    currentField,
    selectAndLinkSelectedOptions,
    currentRelationFromParent,
    unsavedObjects,
    setUnsavedObjects,
    setSavedObjectsToRemove,
    serverFieldValues,
    yesObjects,
    onChange,
    isFetching,
    onScrollToRelationTable
  })

  const handleSave = useRelationClassRelationSave({
    selectAndLinkSelectedOptions,
    changeAndSave,
    currentField,
    relationWorkingSet,
    refetchObjects,
    relationCounterpartColumn,
    fieldValues,
    objectclassUuid,
    onScrollToRelationTable
  })

  const unlinkObjects = useRelationClassRelationUnlink({
    currentField,
    serverFieldValues,
    unsavedObjects,
    relationWorkingSet,
    changeAndSave,
    onChange
  })

  const tableObjects = useMemo(
    () => [
      ...yesObjects.filter(
        (object) =>
          !savedObjectsToRemove.some(
            (objectToRemove) => objectToRemove.uuid === object.uuid
          )
      ),
      ...unsavedObjects
    ],
    [yesObjects, unsavedObjects, savedObjectsToRemove]
  )

  const title = field?.display_text?.des || ''

  const selectedOptionsUnsaved = useMemo(() => {
    const base = selectAndLinkSelectedOptions ?? tableObjects
    if (!base?.length) return []
    const savedUuids = new Set(currentRelationFromParent.map((o) => o.uuid))
    const filtered = base.filter((opt) => !savedUuids.has(opt.uuid))
    return filtered
  }, [selectAndLinkSelectedOptions, tableObjects, currentRelationFromParent])

  const selectAndLinkOptions: SelectAndLinkOptions = useMemo(
    () => ({
      targetComponent: targetComponents.OBJECT_VIEW,
      targetField: currentField,
      selectedOptions: selectedOptionsUnsaved
    }),
    [currentField, selectedOptionsUnsaved]
  )

  if (fieldType === 'card') {
    const handleAutocompleteChange = (options: YesObject[]) => {
      onChange(currentField, options)

      const newSelectedOptions = options.filter(
        (option) => !tableObjects.find((object) => object.uuid === option.uuid)
      )
      setUnsavedObjects((oldUnsavedObjects) => [
        ...oldUnsavedObjects.filter((object) =>
          options.find((option) => option.uuid === object.uuid)
        ),
        ...newSelectedOptions
      ])

      setSavedObjectsToRemove(
        yesObjects.filter(
          (object) => !options.some((option) => option.uuid === object.uuid)
        )
      )
    }

    return (
      <RelationAutocomplete
        {...{
          objectclassUuid,
          selectAndLinkOptions,
          selectAndLinkTargetPath,
          hideSelectAndLinkAction,
          title,
          navigationSliderBackgroundColor,
          navigationSliderColor
        }}
        id={tableId}
        multiplicity={relationFieldRelation?.multiplicity || 'N:N'}
        onChange={handleAutocompleteChange}
        selectedObjects={tableObjects}
        informational={informational || !isEditing}
      />
    )
  }

  return (
    <RelationClassTable
      onSave={handleSave}
      onUnlink={unlinkObjects}
      {...{
        title,
        informational,
        objectclassUuid,
        tableObjects,
        currentField,
        fieldSettings,
        tableId,
        layoutClassificationUuid,
        isFetching,
        hierarchyLevel,
        actionButton,
        hideInsertAction,
        hideSelectAndLinkAction,
        hideDocumentUploadAction,
        selectAndLinkTargetPath,
        id: tableId,
        uploadAcceptedFileTypes,
        selectAndLinkOptions,
        pagination,
        sortOptions,
        totalObjects,
        unsavedObjects,
        setSortOptions,
        setPage,
        refetchObjects,
        navigationSliderBackgroundColor,
        navigationSliderColor
      }}
    />
  )
}

export default RelationClass
